// @spec GHG-STR-007
// @fixed review-001: Use native Select component API instead of Radix UI style
// Emission factor search component with filters

import { AlertCircle, RotateCcw, Sparkles } from 'lucide-react';
import { useMemo } from 'react';

import { useEmissionFactorSearch } from '../api/use-emission-factors';
import {
  useEmissionFactorSearchState,
  type EmissionFactorSearchContext,
} from '../hooks/useEmissionFactorSearchState';
import { rankEmissionFactors } from '../lib/emission-factor-ranking';
import type { EmissionFactor, EmissionFactorSearchParams } from '../types';

import { EmissionFactorCard } from './EmissionFactorCard';
import { SearchFilterToolbar } from './SearchFilterToolbar';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { ApiClientError } from '@/types/api';
import { useTranslation } from 'react-i18next';

interface EmissionFactorSearchProps {
  onSelectFactor?: (factorId: string, factor?: EmissionFactor) => void;
  initialParams?: Partial<EmissionFactorSearchParams>;
  showUsedOnlyToggle?: boolean;
  context?: EmissionFactorSearchContext;
  showContextNarrowing?: boolean;
}

/**
 * Factor source types (custom org factors vs Climatiq database).
 */
function getFactorSourceOptions(_t: (key: string) => string) {
  return [
    { value: '', label: 'All Factors' },
    { value: 'custom', label: 'Custom Factors' },
    { value: 'climatiq', label: 'Climatiq Database' },
  ];
}

/**
 * Common data sources for emission factors.
 */
function getDataSources(t: (key: string) => string) {
  return [
    { value: '', label: t('emissionFactorSearch.allSources') },
    { value: 'EPA', label: t('emissionFactorSearch.epaUs') },
    { value: 'ADEME', label: t('emissionFactorSearch.ademeFrance') },
    { value: 'UBA', label: t('emissionFactorSearch.ubaGermany') },
    { value: 'BEIS', label: t('emissionFactorSearch.beisDefraUk') },
  ];
}

/**
 * Common regions for filtering.
 */
function getRegions(t: (key: string) => string) {
  return [
    { value: '', label: t('emissionFactorSearch.allRegions') },
    { value: 'GLOBAL', label: t('emissionFactorSearch.regionGlobal') },
    { value: 'EU', label: t('emissionFactorSearch.regionEU') },
    { value: 'PL', label: t('emissionFactorSearch.regionPoland') },
    { value: 'DE', label: t('emissionFactorSearch.regionGermany') },
    { value: 'FR', label: t('emissionFactorSearch.regionFrance') },
    { value: 'GB', label: t('emissionFactorSearch.regionUK') },
    { value: 'US', label: t('emissionFactorSearch.regionUS') },
    { value: 'AT', label: t('emissionFactorSearch.regionAustria') },
    { value: 'BE', label: t('emissionFactorSearch.regionBelgium') },
    { value: 'CZ', label: t('emissionFactorSearch.regionCzechRepublic') },
    { value: 'DK', label: t('emissionFactorSearch.regionDenmark') },
    { value: 'ES', label: t('emissionFactorSearch.regionSpain') },
    { value: 'FI', label: t('emissionFactorSearch.regionFinland') },
    { value: 'IE', label: t('emissionFactorSearch.regionIreland') },
    { value: 'IT', label: t('emissionFactorSearch.regionItaly') },
    { value: 'NL', label: t('emissionFactorSearch.regionNetherlands') },
    { value: 'NO', label: t('emissionFactorSearch.regionNorway') },
    { value: 'PT', label: t('emissionFactorSearch.regionPortugal') },
    { value: 'SE', label: t('emissionFactorSearch.regionSweden') },
    { value: 'CH', label: t('emissionFactorSearch.regionSwitzerland') },
  ];
}

/**
 * GWP calculation methods.
 */
function getCalculationMethods(t: (key: string) => string) {
  return [
    { value: '', label: t('emissionFactorSearch.anyMethod') },
    { value: 'ar6', label: t('emissionFactorSearch.ar6Latest') },
    { value: 'ar5', label: t('emissionFactorSearch.ar5') },
    { value: 'ar4', label: t('emissionFactorSearch.ar4') },
  ];
}

/**
 * Unit types for filtering emission factors.
 */
function getUnitTypes(t: (key: string) => string) {
  return [
    { value: '', label: t('emissionFactorSearch.allUnits') },
    { value: 'Energy', label: t('emissionFactorSearch.unitEnergy') },
    { value: 'Weight', label: t('emissionFactorSearch.unitWeight') },
    { value: 'Volume', label: t('emissionFactorSearch.unitVolume') },
    { value: 'Distance', label: t('emissionFactorSearch.unitDistance') },
    { value: 'Area', label: t('emissionFactorSearch.unitArea') },
    { value: 'Money', label: t('emissionFactorSearch.unitMoney') },
    { value: 'Number', label: t('emissionFactorSearch.unitNumber') },
    { value: 'Data', label: t('emissionFactorSearch.unitData') },
    { value: 'Time', label: t('emissionFactorSearch.unitTime') },
    { value: 'Power', label: t('emissionFactorSearch.unitPower') },
    { value: 'WeightOverDistance', label: t('emissionFactorSearch.unitWeightDistance') },
    { value: 'PassengerOverDistance', label: t('emissionFactorSearch.unitPassengerDistance') },
    { value: 'ContainerOverDistance', label: t('emissionFactorSearch.unitContainerDistance') },
  ];
}

/**
 * Emission factor search component with filters and results grid.
 */
export function EmissionFactorSearch({
  onSelectFactor,
  initialParams,
  showUsedOnlyToggle = true,
  context,
  showContextNarrowing = true,
}: EmissionFactorSearchProps) {
  const { t } = useTranslation('ghg');
  const FACTOR_SOURCE_OPTIONS = useMemo(() => getFactorSourceOptions(t), [t]);
  const DATA_SOURCES = useMemo(() => getDataSources(t), [t]);
  const REGIONS = useMemo(() => getRegions(t), [t]);
  const CALCULATION_METHODS = useMemo(() => getCalculationMethods(t), [t]);
  const UNIT_TYPES = useMemo(() => getUnitTypes(t), [t]);
  const searchStateOptions = useMemo(
    () => ({
      ...(initialParams ? { initialParams } : {}),
      ...(context ? { context } : {}),
    }),
    [initialParams, context]
  );
  const {
    searchParams,
    queryInput,
    hasActiveFilters,
    contextChips,
    hasContext,
    isContextApplied,
    setQueryInput,
    handleSearch,
    handleSourceChange,
    handleRegionChange,
    handleMethodChange,
    handleYearChange,
    handleUnitTypeChange,
    handleFactorSourceChange,
    handleUsedOnlyToggle,
    handlePageChange,
    handleResetFilters,
    applyContextPreset,
    clearContextPreset,
  } = useEmissionFactorSearchState(searchStateOptions);

  const { data, isLoading, isFetching, error } = useEmissionFactorSearch(searchParams);
  const usedFactorPriorityQuery = useEmissionFactorSearch(
    {
      usedOnly: true,
      page: 1,
      pageSize: 100,
      ...(searchParams.query ? { query: searchParams.query } : {}),
      ...(searchParams.region ? { region: searchParams.region } : {}),
      ...(searchParams.year !== undefined ? { year: searchParams.year } : {}),
    },
    {
      enabled: Boolean(data) && !searchParams.usedOnly,
    }
  );
  const usedFactorIds = useMemo(
    () => new Set((usedFactorPriorityQuery.data?.items ?? []).map((factor) => factor.id)),
    [usedFactorPriorityQuery.data?.items]
  );
  const rankedItems = useMemo(
    () =>
      rankEmissionFactors({
        factors: data?.items ?? [],
        ...(context ? { context } : {}),
        usedFactorIds,
        ...(searchParams.query !== undefined ? { query: searchParams.query } : {}),
      }),
    [context, data?.items, searchParams.query, usedFactorIds]
  );

  const errorMessage =
    error instanceof ApiClientError
      ? error.status === 403
        ? t('emissionFactorSearch.error403')
        : error.status === 401
          ? t('emissionFactorSearch.error401')
          : error.message
      : t('emissionFactorSearch.errorFallback');

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-md border bg-muted/10 p-4">
        <div className="space-y-1">
          <p className="text-sm font-medium">{t('emissionFactorSearch.title')}</p>
          <p className="text-xs text-muted-foreground">
            {t('emissionFactorSearch.description')}
          </p>
        </div>

        {showContextNarrowing && hasContext ? (
          <div className="space-y-3 rounded-md border bg-background/70 p-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5" />
              <span>{t('emissionFactorSearch.activityContext')}</span>
              {contextChips.map((chip) => (
                <span key={chip} className="rounded-full border bg-muted/40 px-2 py-0.5">
                  {chip}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={applyContextPreset}
                disabled={isFetching || isContextApplied}
              >
                {t('emissionFactorSearch.narrowToContext')}
              </Button>
              {isContextApplied ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={clearContextPreset}
                  disabled={isFetching}
                >
                  {t('emissionFactorSearch.clearContext')}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        <SearchFilterToolbar
          query={queryInput}
          onQueryChange={setQueryInput}
          queryPlaceholder={t('emissionFactorSearch.searchPlaceholder')}
          queryDisabled={isFetching}
          querySectionClassName="flex flex-wrap items-end gap-2"
          queryContainerClassName="min-w-80 flex-1 gap-2"
          filtersPlacement="inline"
          onSubmit={handleSearch}
          submitDisabled={isFetching}
          submitLoading={isFetching}
        >
          <Select
            options={FACTOR_SOURCE_OPTIONS}
            value={searchParams.factorSource ?? ''}
            onChange={handleFactorSourceChange}
            placeholder={t('emissionFactorSearch.factorType')}
            className="w-40"
            disabled={isFetching}
          />

          <Select
            options={DATA_SOURCES}
            value={searchParams.source ?? ''}
            onChange={handleSourceChange}
            placeholder={t('placeholder.dataSource')}
            className="w-40"
            disabled={isFetching}
          />

          <Select
            options={REGIONS}
            value={searchParams.region ?? ''}
            onChange={handleRegionChange}
            placeholder={t('placeholder.region2')}
            className="w-40"
            disabled={isFetching}
          />

          <Select
            options={CALCULATION_METHODS}
            value={searchParams.calculationMethod ?? ''}
            onChange={handleMethodChange}
            placeholder={t('placeholder.gwpMethod')}
            className="w-36"
            disabled={isFetching}
          />

          <Select
            options={UNIT_TYPES}
            value={searchParams.unitType ?? ''}
            onChange={handleUnitTypeChange}
            placeholder={t('placeholder.unitType')}
            className="w-44"
            disabled={isFetching}
          />

          <Input
            type="number"
            placeholder={t('emissionFactorSearch.placeholder_year')}
            className="w-28"
            value={searchParams.year ?? ''}
            onChange={handleYearChange}
            disabled={isFetching}
          />
        </SearchFilterToolbar>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-4">
            <p className="text-xs text-muted-foreground">
              {t('emissionFactorSearch.clickFactorHint')}
            </p>
            {showUsedOnlyToggle ? (
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Switch
                  checked={Boolean(searchParams.usedOnly)}
                  onCheckedChange={handleUsedOnlyToggle}
                  disabled={isFetching}
                />
                {t('emissionFactorSearch.usedInOrgOnly')}
              </label>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetFilters}
            disabled={!hasActiveFilters || isFetching}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            {t('emissionFactorSearch.clearFilters')}
          </Button>
        </div>
      </div>

      {/* Results Info */}
      {data ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-background/70 px-3 py-2 text-sm text-muted-foreground">
          <span>
            {t('emissionFactorSearch.resultsCount', { count: data.pagination.totalItems })}
            {data.fromCache ? ` ${t('emissionFactorSearch.fromCache')}` : ''}
          </span>
          <span>
            {searchParams.usedOnly
              ? t('emissionFactorSearch.usedFactorsMode')
              : t('emissionFactorSearch.fullSearchMode')}
          </span>
          <span>{t('emissionFactorSearch.dataVersion', { version: data.dataVersion })}</span>
        </div>
      ) : null}

      {/* Error State */}
      {error ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/5 p-8 text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-muted-foreground">{errorMessage}</p>
        </div>
      ) : null}

      {/* Results Grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      ) : rankedItems.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground">
            {searchParams.usedOnly
              ? t('emissionFactorSearch.noUsedFactors')
              : t('emissionFactorSearch.noFactorsFound')}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {searchParams.usedOnly
              ? t('emissionFactorSearch.turnOffUsedOnly')
              : t('emissionFactorSearch.tryAdjusting')}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rankedItems.map((factor) => (
            <EmissionFactorCard
              key={factor.id}
              factor={factor}
              onClick={() => {
                onSelectFactor?.(factor.id, factor);
              }}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={searchParams.page === 1 || isFetching}
            onClick={() => {
              handlePageChange((searchParams.page ?? 1) - 1);
            }}
          >
            {t('emissionFactorSearch.btn_previous')}
          </Button>
          <span className="px-4 text-sm text-muted-foreground">
            {t('emissionFactorSearch.pageOf', { page: data.pagination.page, total: data.pagination.totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={searchParams.page === data.pagination.totalPages || isFetching}
            onClick={() => {
              handlePageChange((searchParams.page ?? 1) + 1);
            }}
          >
            {t('emissionFactorSearch.btn_next')}
          </Button>
        </div>
      )}
    </div>
  );
}
